import React, { useState, useEffect, useMemo } from "react";
import { reportsAPI } from "../../utils/reportsAPI";
import { useAuth } from "../../context/AuthContext";
import Pagination from "../../components/Pagination";
import { EmptyState, ErrorState } from "../../components/EmptyStates";
import ReportContent from "../../components/ReportContent";
import { useTranslation } from "react-i18next";
import "./Reports.css";

function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const { t } = useTranslation();

  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [user]);

  useEffect(() => {
    loadReports(currentPage);
  }, [effectiveOrgId, currentPage]);

  const loadReports = async (page = 1) => {
    if (!effectiveOrgId) {
      setError(
        t("reports.noOrganization", "No organization selected"),
      );
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await reportsAPI.list(effectiveOrgId, {
        page,
        page_size: 10,
      });
      const data = response?.data || response;
      const reportsList = Array.isArray(data) ? data : data?.results || [];
      setReports(reportsList);

      if (response?.pagination) {
        setPagination(response.pagination);
      }

      setError(null);
    } catch (err) {
      setError(err.message || t("reports.loadFailed", "Failed to load reports"));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewReport = async (report) => {
    if (!effectiveOrgId || !report?.id) {
      setSelectedReport(report);
      return;
    }


    setReports((prev) =>
      prev.map((r) =>
        r.id === report.id ? { ...r, is_seen: true } : r,
      ),
    );

    try {
      // Tell backend this report is seen for this user
      await reportsAPI.markSeen(effectiveOrgId, report.id);
    } catch {
      // Ignore errors here; we already updated local UI
    }

    setSelectedReport(report);
  };

  if (selectedReport) {
    return (
      <div className="page-content">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSelectedReport(null)}
          style={{ marginBottom: "16px" }}
        >
          ← {t("reports.backToList", "Back to Reports")}
        </button>

        <div className="card">
          <h2 className="report-title">
            {selectedReport.title || selectedReport.name}
          </h2>
          <div className="report-meta">
            <span>
              {new Date(
                selectedReport.created_at || Date.now(),
              ).toLocaleDateString()}
              {(selectedReport.author_name || selectedReport.author_email) &&
                ` • By ${selectedReport.author_name || selectedReport.author_email}`}
            </span>
          </div>

          <div className="report-content">
            <h3>{t("reports.status", "Status")}</h3>
            <p>{selectedReport.status || "N/A"}</p>

            {selectedReport.description && (
              <>
                <h3>{t("reports.description", "Description")}</h3>
                <p>{selectedReport.description}</p>
              </>
            )}

            <h3>{t("reports.content", "Content")}</h3>
            <ReportContent
              report={selectedReport}
              organizationId={effectiveOrgId}
            />

            <div className="chart-placeholder">
              {t("reports.generatedAt", "Report generated: ")}
              {new Date(
                selectedReport.created_at || Date.now(),
              ).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{t("nav.reports")}</h2>
        </div>

        <div className="reports-list">
          {loading ? (
            <p>{t("reports.loading", "Loading reports...")}</p>
          ) : error ? (
            <ErrorState error={error} onRetry={loadReports} />
          ) : reports.length > 0 ? (
            reports.map((report) => (
              <div
                key={report.id}
                className={`report-item${
                  report.is_seen === false ? " report-item-unseen" : ""
                }`}
              >
                <div className="report-info">
                  <h3 className="report-item-title">
                    {report.title || report.name}
                  </h3>
                  <p className="report-item-meta">
                    {report.created_at
                      ? new Date(report.created_at).toLocaleDateString()
                      : "N/A"}
                    {report.author_name || report.author_email
                      ? ` • By ${report.author_name || report.author_email}`
                      : ""}
                  </p>
                </div>
                <div className="report-actions">
                  <span
                    className={`status-badge status-${
                      report.status || "completed"
                    }`}
                  >
                    {report.status || t("reports.completed", "completed")}
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleViewReport(report)}
                  >
                    {t("actions.viewReport")}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <EmptyState
              title={t("reports.emptyTitle", "No reports available")}
              message={t(
                "reports.emptyMessage",
                "Reports generated by your analyst will appear here.",
              )}
            />
          )}
        </div>
        {pagination && reports.length > 0 && (
          <Pagination pagination={pagination} onPageChange={handlePageChange} />
        )}
      </div>
    </div>
  );
}

export default Reports;
